import {
  Component,
  Host,
  h,
  Element,
  Prop,
  State,
  Watch,
  Event,
  EventEmitter,
} from '@stencil/core';
import { clamp } from '../../utils/clamp';

import { decodeColor, mixColorForTheme } from '@aha-app/aha-shared';

export interface AhaRoadmapBar extends HTMLAhaRoadmapBarElement {}

/**
 * A component that shows a roadmap bar or milestone, optionally with progress toward a goal.
 *
 * @slot default - Contents of the default slot.
 */
@Component({
  tag: 'aha-roadmap-bar',
  styleUrl: 'aha-roadmap-bar.css',
  shadow: true,
})
export class AhaRoadmapBar {
  @Element() host!: HTMLAhaRoadmapBarElement;

  private barElem?: HTMLElement;
  private labelElem?: HTMLElement;
  private placeholderElem?: HTMLElement;

  get hasProgress() {
    return this.progress || this.progress === 0;
  }

  /** How much progress should be shown on the progress bar (0-100). */
  @Prop({ reflect: true }) progress?: number | undefined | null;
  @Watch('progress')
  private clampProgress() {
    if (this.progress || this.progress === 0) {
      this.progress = clamp(0, this.progress, 100);
      queueMicrotask(() => {
        if (this.progress || this.progress === 0)
          this.host.setAttribute('progress', this.progress.toString());
      });
    }
  }

  /** The base color for the bar. The progress bar color, the gutter color, and the border color will be derived as shades of this base color. Can be expressed as any value CSS color value (hex, rgba, CSS variable, etc.) */
  @Prop({ reflect: true }) color: string = 'rgb(174, 209, 134)'; // Corresponds to var(--aha-green-500)

  @Watch('color')
  private setAdditionalColors() {
    const decodedColor = decodeColor(this.color);
    this.barColor = decodedColor;
    this.gutterColor = mixColorForTheme(decodedColor, 70);
    this.borderColor = mixColorForTheme(decodedColor, 30, true);
  }

  /** Whether or not the bar has a definite start. When set, this provides a rounded corner "cap" at the start of the bar. */
  @Prop({ reflect: true }) start?: boolean;

  /** Whether or not the bar has a definite end. When set, this provides a rounded corner "cap" at the end of the bar. */
  @Prop({ reflect: true }) end?: boolean;

  /** Whether or not the bar should be shown as a "milestone" (a small circle, rather than a bar). */
  @Prop({ reflect: true }) milestone?: boolean;

  /** Is this a parent container of other bars. */
  @Prop({ reflect: true }) container: boolean = false;

  @State() baseColor?: string;

  @State() barColor?: string;

  @State() gutterColor?: string;

  @State() borderColor?: string;

  @State() labelVisible?: boolean;

  @State() renderedWidth?: number;

  @State() renderedHeight?: number;

  @State() dimensions?: WeakMap<Element, DOMRect>;

  @State() resizeObserver?: ResizeObserver;

  @Watch('labelVisible')
  private emitLabelEvent() {
    if (this.labelVisible) {
      this.labelShownEvent.emit({ host: this });
    } else {
      this.labelHiddenEvent.emit({ host: this });
    }
  }

  /**
   * Triggers when the roadmap bar's label has been hidden.
   */
  @Event({
    eventName: 'aha-roadmap-bar:label-hidden',
    cancelable: true,
    composed: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  labelHiddenEvent!: EventEmitter;

  /**
   * Triggers when the roadmap bar's label has been shown.
   */
  @Event({
    eventName: 'aha-roadmap-bar:label-shown',
    cancelable: true,
    composed: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  labelShownEvent!: EventEmitter;

  componentDidLoad() {
    this.setAdditionalColors();
  }

  componentDidRender() {
    this.setupResizeObserver();
  }

  setupResizeObserver() {
    // Only need to set this up if it has not been already
    if (this.resizeObserver) {
      return;
    }

    // We have to wait until the barElem is available
    if (!this.barElem) {
      return;
    }

    this.dimensions = new WeakMap();

    this.resizeObserver = new ResizeObserver(entries => {
      if (!this.dimensions) {
        return;
      }

      entries.forEach(entry => {
        this?.dimensions?.set(entry.target, entry.contentRect);
      });

      this.calculateDimensionsAndLabelVisibility(this.dimensions);
    });

    if (this.labelElem) {
      this.resizeObserver.observe(this.labelElem);
    }

    this.resizeObserver.observe(this.barElem);
  }

  // When the container attribute changes, we want have to redo the calculations
  // for the barElem.
  @Watch('container')
  resetObserver() {
    this.barElem = undefined;

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = undefined;
    }
  }

  private calculateDimensionsAndLabelVisibility(dimensions: WeakMap<Element, DOMRect>) {
    // Get the dimensions of the bar for the container type. The container
    // type one is an SVG, so we need to know the exact dimensions when
    // calculating path points.
    if (this.barElem && this.container) {
      const barDimensions = dimensions.get(this.barElem);

      if (barDimensions) {
        this.renderedWidth = barDimensions.width;
        this.renderedHeight = barDimensions.height;
      }
    }

    // If the label is wider than the bar, we need to hide it.
    if (this.barElem && this.labelElem) {
      // Get the bounding rects for the bar and the label.
      const barRect = dimensions.get(this.barElem);
      const labelRect = dimensions.get(this.labelElem);

      if (labelRect) {
        if (labelRect.width && barRect) {
          if (labelRect.width > barRect.width || labelRect.height > barRect.height) {
            this.labelVisible = false;
          } else {
            this.labelVisible = true;
          }
        } else {
          this.labelVisible = false;
        }
      }
    }
  }

  render() {
    let bar;

    if (this.container) {
      bar = this.renderContainer();
    } else {
      bar = (
        <div
          id="bar"
          ref={el => (this.barElem = el)}
          style={{
            '--aha-roadmap-bar--bar-color': this.barColor,
            '--aha-roadmap-bar--gutter-color': this.gutterColor,
            '--aha-roadmap-bar--border-color': this.borderColor,
            '--aha-roadmap-bar--progress':
              this.progress || this.progress === 0 ? `${this.progress}%` : '',
          }}
        >
          <div id="gutter">
            <div id="progress"></div>
          </div>
          <div id="label" part="label" ref={el => (this.labelElem = el)}>
            <slot></slot>
          </div>
        </div>
      );
    }

    return (
      <Host label-visible={this.labelVisible}>
        <div id="color" style={{ backgroundColor: this.color }}></div>
        {bar}
      </Host>
    );
  }

  private renderContainer() {
    let svg;

    if (this.renderedWidth && this.renderedHeight) {
      const BORDER_RADIUS = 4;
      const SLOPE_HEIGHT = this.renderedHeight / 2;
      const BAR_HEIGHT = this.renderedHeight / 2;
      const SLOPE_WIDTH = SLOPE_HEIGHT / Math.tan(-40);

      let slopeWidth;

      const teethCollapsing = this.renderedWidth - 2 * BORDER_RADIUS < 2 * SLOPE_WIDTH;
      const width = this.renderedWidth < 2 * BORDER_RADIUS ? 2 * BORDER_RADIUS : this.renderedWidth;

      let slopeRise;

      // Strokes in SVGs are centered on the given path, so it's half on each
      // side. If we want a 1px stroke, we need to give coordinates aligned to
      // a 0.5px grid. For example, if we want 4px wide with a border of 1px
      // on each side (border-box so 4 px in total), then we would draw a path
      // at 0.5 and 3.5 with stroke width 1.

      if (teethCollapsing) {
        slopeWidth = Math.max(0, (width - 2 * BORDER_RADIUS) / 2);
        slopeRise = (slopeWidth * SLOPE_HEIGHT) / SLOPE_WIDTH;
      } else {
        slopeWidth = SLOPE_WIDTH;
        slopeRise = SLOPE_HEIGHT;
      }

      const toothWidth = slopeWidth + BORDER_RADIUS;

      let path = `
        M ${BORDER_RADIUS + 0.5} 0.5
        Q 0.5 0.5, 0.5 ${BORDER_RADIUS + 0.5}
        l 0 ${SLOPE_HEIGHT + BAR_HEIGHT - 2 * BORDER_RADIUS - 1}
        q 0 ${BORDER_RADIUS}, ${BORDER_RADIUS} ${BORDER_RADIUS}
      `;

      // Only draw the line up if there is enough space. If there isn't enough
      // space we are basically just drawing a rectangle with no teeth.
      if (slopeWidth > 0) {
        path += ` l ${slopeWidth} -${slopeRise}`;
      }

      // Draw the rest of the bar between the teeth if there is enough space.
      if (!teethCollapsing) {
        path += ` l ${this.renderedWidth - 2 * toothWidth} 0`;
      }

      path += `
        L ${width - 0.5 - BORDER_RADIUS} ${0.5 + SLOPE_HEIGHT + BAR_HEIGHT - 1}
        q ${BORDER_RADIUS} 0, ${BORDER_RADIUS} -${BORDER_RADIUS}
        L ${width - 0.5} ${BORDER_RADIUS + 0.5}
        q 0 -${BORDER_RADIUS}, -${BORDER_RADIUS} -${BORDER_RADIUS} Z
      `;

      const hasUndefinedEnd = !this.start || !this.end;

      // 30px when the bar is wide enough, otherwise just 30% when the bar is small
      const gradientWidth = Math.min(30 / width, 0.3);

      let endsMask;
      let endsGradient;

      if (!this.start && !this.end) {
        endsGradient = (
          <linearGradient id="ends-gradient">
            <stop offset="0%" stop-color="white" stop-opacity="0"></stop>
            <stop offset={gradientWidth} stop-color="white" stop-opacity="1"></stop>
            <stop offset={1 - gradientWidth} stop-color="white" stop-opacity="1"></stop>
            <stop offset="100%" stop-color="white" stop-opacity="0"></stop>
          </linearGradient>
        );
        endsMask = (
          <mask id="ends-mask">
            <rect width={`${width}px`} height="100%" x="0" y="0" fill="url(#ends-gradient)"></rect>
          </mask>
        );
      } else if (!this.end) {
        endsGradient = (
          <linearGradient id="ends-gradient">
            <stop offset={1 - gradientWidth} stop-color="white" stop-opacity="1"></stop>
            <stop offset="100%" stop-color="white" stop-opacity="0"></stop>
          </linearGradient>
        );
        endsMask = (
          <mask id="ends-mask">
            <rect width={`${width}px`} height="100%" x="0" y="0" fill="url(#ends-gradient)"></rect>
          </mask>
        );
      } else if (!this.start) {
        endsGradient = (
          <linearGradient id="ends-gradient">
            <stop offset="0%" stop-color="white" stop-opacity="0"></stop>
            <stop offset={gradientWidth} stop-color="white" stop-opacity="1"></stop>
          </linearGradient>
        );
        endsMask = (
          <mask id="ends-mask">
            <rect width={`${width}px`} height="100%" x="0" y="0" fill="url(#ends-gradient)"></rect>
          </mask>
        );
      }

      svg = (
        <svg
          width={width}
          height={this.renderedHeight}
          style={{
            position: 'absolute',
            top: '0',
            filter: 'drop-shadow(0px 0px 8px rgba(0, 0, 0, 0.08))',
          }}
        >
          <defs>
            <clipPath id="bar-clip">
              <path d={path} stroke="blue" stroke-width="1" />
            </clipPath>

            {endsGradient}
            {endsMask}
          </defs>

          <rect
            width="100%"
            height="100%"
            fill={this.gutterColor}
            clip-path="url(#bar-clip)"
            mask={hasUndefinedEnd ? 'url(#ends-mask)' : undefined}
          />
          <rect
            width={this.progress || this.progress === 0 ? (width * this.progress) / 100 : width}
            height="100%"
            fill={this.barColor}
            clip-path="url(#bar-clip)"
            mask={hasUndefinedEnd ? 'url(#ends-mask)' : undefined}
          />
          <path
            d={path}
            stroke={this.borderColor}
            stroke-width="1"
            fill="transparent"
            stroke-linejoin="round"
            mask={hasUndefinedEnd ? 'url(#ends-mask)' : undefined}
          />
        </svg>
      );
    }

    return (
      <span style={{ position: 'relative' }}>
        {svg}
        <div
          id="size-template"
          ref={el => (this.barElem = el)}
          style={{ width: '100%', height: '100%', display: 'inline-block' }}
        />
      </span>
    );
  }
}
